import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { Mail, Eye, EyeOff, Loader2, RefreshCw, AlertCircle } from 'lucide-react'
import { ApiResponse } from '../../services/api'

interface LoginFormProps {
  onSendCredentials: (email: string) => Promise<ApiResponse<any>>
  onLogin: (email: string, password: string, rememberMe?: boolean) => Promise<ApiResponse<any>>
  onResetPassword?: (email: string) => Promise<ApiResponse<any>>
  isLoading: boolean
}

interface LoginFormData {
  email: string
  password?: string
  rememberMe?: boolean
}

interface ErrorState {
  type: 'success' | 'error' | 'warning'
  code?: string
  message: string
  details?: any
  retryable?: boolean
  troubleshooting?: string[]
}

export default function LoginForm({ onSendCredentials, onLogin, onResetPassword, isLoading }: LoginFormProps) {
  const [mode, setMode] = useState<'request' | 'login' | 'reset'>('login')
  const [showPassword, setShowPassword] = useState(false)
  const [errorState, setErrorState] = useState<ErrorState | null>(null)
  const [isRetrying, setIsRetrying] = useState(false)
  
  const { register, handleSubmit, formState: { errors }, watch } = useForm<LoginFormData>()


  const handleSendCredentials = async (data: LoginFormData) => {
    setErrorState(null)
    const response = await onSendCredentials(data.email)
    
    if (response && !response.error) {
      setErrorState({
        type: 'success',
        message: 'Login credentials have been sent to your email address. Please check your inbox.'
      })
      setMode('login')
    } else if (response && response.error) {
      setErrorState({
        type: 'error',
        code: response.error.code,
        message: getErrorMessage(response.error, 'send credentials'),
        details: response.error.details,
        retryable: response.error.retryable,
        troubleshooting: response.error.details?.troubleshooting
      })
    } else {
      setErrorState({
        type: 'error',
        code: 'API_ERROR',
        message: 'Failed to send credentials. Please try again.',
        retryable: true
      })
    }
  }

  const handleLogin = async (data: LoginFormData) => {
    if (!data.password) return
    
    setErrorState(null)
    const response = await onLogin(data.email, data.password, data.rememberMe || false)
    
    if (response && response.error) {
      setErrorState({
        type: 'error',
        code: response.error.code,
        message: getErrorMessage(response.error, 'login'),
        details: response.error.details,
        retryable: response.error.retryable,
        troubleshooting: response.error.details?.troubleshooting
      })
    } else if (!response) {
      setErrorState({
        type: 'error',
        code: 'API_ERROR',
        message: 'Login failed. Please try again.',
        retryable: true
      })
    }
  }

  const handleResetPassword = async (data: LoginFormData) => {
    if (!onResetPassword) return
    
    setErrorState(null)
    const response = await onResetPassword(data.email)
    
    if (response && !response.error) {
      setErrorState({
        type: 'success',
        message: 'Password reset instructions have been sent to your email address. Please check your inbox and follow the instructions to reset your password.'
      })
      // Switch back to login mode after successful reset request
      setTimeout(() => {
        setMode('login')
        setErrorState(null)
      }, 3000)
    } else if (response && response.error) {
      setErrorState({
        type: 'error',
        code: response.error.code,
        message: getErrorMessage(response.error, 'reset password'),
        details: response.error.details,
        retryable: response.error.retryable,
        troubleshooting: response.error.details?.troubleshooting
      })
    } else {
      setErrorState({
        type: 'error',
        code: 'API_ERROR',
        message: 'Failed to send reset instructions. Please try again.',
        retryable: true
      })
    }
  }

  const handleRetry = async () => {
    if (!errorState?.retryable || isRetrying) return
    
    setIsRetrying(true)
    setErrorState(null)
    
    try {
      const email = watch('email')
      const password = watch('password')
      
      if (mode === 'request') {
        await handleSendCredentials({ email })
      } else if (mode === 'login' && password) {
        const rememberMe = watch('rememberMe')
        await handleLogin({ email, password, rememberMe })
      } else if (mode === 'reset') {
        await handleResetPassword({ email })
      }
    } finally {
      setIsRetrying(false)
    }
  }

  const getErrorMessage = (error: any, action: string): string => {
    switch (error.code) {
      case 'CONFIG_ERROR':
        return 'The application is not properly configured. Please contact your system administrator.'
      case 'NETWORK_ERROR':
        return `Unable to connect to the server. Please check your internet connection and try again.`
      case 'CORS_ERROR':
        return 'There is a configuration issue preventing the request. Please contact your system administrator.'
      case 'AUTH_ERROR':
        if (action === 'login') {
          return 'Invalid email or password. Please check your credentials and try again.'
        }
        if (action === 'reset password') {
          return 'Password reset failed. Please try again or contact support.'
        }
        return 'Authentication failed. Please try again or contact support.'
      case 'API_ERROR':
        if (error.details?.status === 429) {
          return 'Too many requests. Please wait a moment before trying again.'
        }
        if (error.details?.status >= 500) {
          return 'Server error occurred. Please try again in a few moments.'
        }
        return error.message || `Failed to ${action}. Please try again.`
      default:
        return error.message || `An unexpected error occurred while trying to ${action}.`
    }
  }

  return (
    <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-6">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-semibold text-gray-900">
          {mode === 'request' ? 'Pilot Login' : mode === 'login' ? 'Enter Your Credentials' : 'Reset Password'}
        </h2>
        <p className="text-sm text-gray-600 mt-2">
          {mode === 'request' 
            ? 'Enter your registered email to receive login credentials'
            : mode === 'login'
            ? 'Use the credentials sent to your email'
            : 'Enter your email address to receive password reset instructions'
          }
        </p>
      </div>

      {errorState && (
        <div className={`mb-4 p-4 rounded-md border ${
          errorState.type === 'success' 
            ? 'bg-green-50 border-green-200 text-green-800' 
            : errorState.type === 'warning'
            ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
            : 'bg-red-50 border-red-200 text-red-800'
        }`}>
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 mt-0.5 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">{errorState.message}</p>
              
              {errorState.troubleshooting && errorState.troubleshooting.length > 0 && (
                <div className="mt-2">
                  <p className="text-xs font-medium mb-1">Troubleshooting:</p>
                  <ul className="text-xs space-y-1">
                    {errorState.troubleshooting.map((tip, index) => (
                      <li key={index} className="flex items-start">
                        <span className="mr-2">•</span>
                        <span>{tip}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {errorState.retryable && (
                <button
                  onClick={handleRetry}
                  disabled={isRetrying || isLoading}
                  className="mt-3 inline-flex items-center px-3 py-1 text-xs font-medium rounded-md bg-white border border-current hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isRetrying ? (
                    <>
                      <Loader2 className="animate-spin h-3 w-3 mr-1" />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Try Again
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit(
        mode === 'request' ? handleSendCredentials : 
        mode === 'login' ? handleLogin : 
        handleResetPassword
      )} className="space-y-4">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <div className="relative">
            <input
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address'
                }
              })}
              type="email"
              id="email"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:border-fwo-blue"
              placeholder="pilot@example.com"
            />
            <Mail className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        {mode === 'login' && (
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
              Password
            </label>
            <div className="relative">
              <input
                {...register('password', {
                  required: 'Password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters'
                  }
                })}
                type={showPassword ? 'text' : 'password'}
                id="password"
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:border-fwo-blue"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && (
              <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
            )}
          </div>
        )}

        {mode === 'login' && (
          <div className="flex items-center">
            <input
              {...register('rememberMe')}
              type="checkbox"
              id="rememberMe"
              className="h-4 w-4 text-fwo-blue focus:ring-fwo-blue border-gray-300 rounded"
            />
            <label htmlFor="rememberMe" className="ml-2 block text-sm text-gray-700">
              Keep me logged in
            </label>
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading}
          className="w-full bg-fwo-blue text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-fwo-blue focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
        >
          {isLoading ? (
            <>
              <Loader2 className="animate-spin h-4 w-4 mr-2" />
              {mode === 'request' ? 'Sending...' : mode === 'login' ? 'Logging in...' : 'Sending Reset Instructions...'}
            </>
          ) : (
            mode === 'request' ? 'Send Login Credentials' : mode === 'login' ? 'Login' : 'Reset Password'
          )}
        </button>
      </form>

      {mode === 'login' && (
        <div className="mt-4 text-center space-y-2">
          <button
            type="button"
            onClick={() => {
              setMode('request')
              setErrorState(null)
            }}
            className="text-sm text-fwo-blue hover:text-blue-700 underline block mx-auto"
          >
            First time? Request login credentials
          </button>
          {onResetPassword && (
            <button
              type="button"
              onClick={() => {
                setMode('reset')
                setErrorState(null)
              }}
              className="text-sm text-fwo-blue hover:text-blue-700 underline block mx-auto"
            >
              Forgot Password?
            </button>
          )}
        </div>
      )}

      {mode === 'reset' && (
        <div className="mt-4 text-center">
          <button
            type="button"
            onClick={() => {
              setMode('login')
              setErrorState(null)
            }}
            className="text-sm text-fwo-blue hover:text-blue-700 underline"
          >
            Back to Login
          </button>
        </div>
      )}

      <div className="mt-6 text-xs text-gray-500 text-center">
        <p>Only registered pilots from Airtribune can access this portal.</p>
        <p className="mt-1">Having trouble? Contact the competition organizers.</p>
      </div>
    </div>
  )
}